import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useTranslation } from 'react-i18next'
import { apiGetBlob, USE_MOCK_API } from '../api/httpClient'

export interface MapMarker {
  id: string
  lat: number
  lng: number
  title: string
  subtitle?: string
  color?: string
  dayIndex?: number
}

const attribution = 'Powered by <a href="https://www.geoapify.com/" target="_blank" rel="noopener noreferrer">Geoapify</a> | <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap contributors</a>'

class QuotaTileLayer extends L.TileLayer {
  protected override createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
    const tile = document.createElement('img')
    tile.alt = ''
    void apiGetBlob(`/maps/tiles/${coords.z}/${coords.x}/${coords.y}.png`)
      .then(blob => {
        const url = URL.createObjectURL(blob)
        tile.onload = () => { URL.revokeObjectURL(url); done(undefined, tile) }
        tile.onerror = () => { URL.revokeObjectURL(url); done(new Error('map tile unavailable'), tile) }
        tile.src = url
      })
      .catch(error => done(error instanceof Error ? error : new Error('map tile unavailable'), tile))
    return tile
  }
}

/** The old filename remains for existing imports; maps now use Geoapify Free tiles. */
export function GeoapifyMap({ markers, height = '70vh', interactive = true, lines = false, onMapClick }:
  { markers: MapMarker[]; height?: string; interactive?: boolean; lines?: boolean;
    onMapClick?: (lat: number, lng: number) => void }) {
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const onMapClickRef = useRef(onMapClick)
  onMapClickRef.current = onMapClick
  const markersRef = useRef(markers)
  markersRef.current = markers
  const [ready, setReady] = useState(0)
  const [error, setError] = useState('')
  const hasMarkers = markers.length > 0

  useEffect(() => {
    if (USE_MOCK_API || !hasMarkers) return
    let disposed = false
    let map: L.Map | null = null
    function init() {
      try {
        if (disposed || !ref.current || markersRef.current.length === 0) return
        const first = markersRef.current[0]
        map = L.map(ref.current, {
          zoomControl: interactive,
          dragging: interactive,
          scrollWheelZoom: interactive,
          doubleClickZoom: interactive,
          boxZoom: interactive,
          keyboard: interactive,
        }).setView([first.lat, first.lng], 13)
        mapRef.current = map
        const tiles = new QuotaTileLayer('', { maxZoom: 20, attribution })
        tiles.on('tileerror', () => { if (!disposed) setError(t('map.unavailable')) })
        tiles.addTo(map)
        map.on('click', event => onMapClickRef.current?.(event.latlng.lat, event.latlng.lng))
        setReady(value => value + 1)
      } catch (err) {
        if (!disposed) setError(err instanceof Error ? err.message : String(err))
      }
    }
    init()
    return () => { disposed = true; map?.remove(); mapRef.current = null }
  }, [hasMarkers, interactive, t])

  useEffect(() => {
    const map = mapRef.current
    if (!map || !ready) return
    const layer = L.layerGroup().addTo(map)
    const bounds = L.latLngBounds(markers.map(marker => [marker.lat, marker.lng] as [number, number]))
    for (const marker of markers) {
      const dot = L.circleMarker([marker.lat, marker.lng], {
        radius: marker.id === 'user' ? 7 : 9,
        color: '#ffffff', weight: 2, fillColor: marker.color ?? '#E07A5F', fillOpacity: 1,
      }).addTo(layer)
      if (interactive) {
        const content = document.createElement('div')
        const name = document.createElement('strong'); name.textContent = marker.title; content.append(name)
        if (marker.subtitle) {
          const detail = document.createElement('p'); detail.textContent = marker.subtitle; content.append(detail)
        }
        dot.bindPopup(content)
      }
    }
    if (markers.length > 1) map.fitBounds(bounds.pad(0.15), { maxZoom: 14 })
    else if (markers.length === 1) map.setView([markers[0].lat, markers[0].lng], 13)
    if (lines) {
      const days = [...new Set(markers.map(marker => marker.dayIndex).filter((day): day is number => day != null && day >= 0))]
      for (const day of days) {
        const ordered = markers.filter(marker => marker.dayIndex === day)
        if (ordered.length > 1) L.polyline(ordered.map(marker => [marker.lat, marker.lng]),
          { color: '#3182CE', opacity: 0.6, weight: 3 }).addTo(layer)
      }
    }
    return () => { map.removeLayer(layer) }
  }, [markers, interactive, lines, ready])

  if (markers.length === 0) return null
  if (USE_MOCK_API) return <div className="flex items-center justify-center bg-st-surface-2 p-3 text-center text-sm text-st-muted" style={{ height }}>
    {t('map.connected_preview')} {markers.map(marker => marker.title).join(' · ')}
  </div>
  return <div className="relative border border-st-line bg-st-surface-2" style={{ height }}>
    <div ref={ref} role="img" aria-label={t('map.aria')} dir="ltr" className="h-full w-full" />
    {error && <div role="status" className="absolute inset-0 flex items-center justify-center bg-st-surface-2 p-4 text-center text-sm text-st-muted">
      {t('map.unavailable')}: {error}</div>}
  </div>
}
